import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { Popup } from '../common/Popup';
import { Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DisplaySettings, Edit } from '@mui/icons-material';
import { NewFeatureBadge } from '../common/NewFeatureBadge';
import { ctxPluto } from '@/contexts/PlutoContext';
import { useContext, useEffect, useState } from 'react';
import { ctxUser } from '@/contexts/UserContext';
import { isNotTokenUser } from '@/utilities/preconditions';

import {
  SettingsColumnPaper,
  SettingsGroupTitle,
  settingsIconSx,
  SettingsLink,
  SettingsPaper,
  SettingsPaperTitle,
  settingsStartIconSx,
} from '../settings/SettingsComponents.style';
import {
  CommonPageContainer,
  CommonPageHeader,
  CommonPageScrollableContent,
} from '../common/CommonPageComponents.style';

export default function SysAdminPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const [sliderIndex, setSliderIndex] = useState(0);
  const plutoCtx = useContext(ctxPluto);
  const ctx = useContext(ctxUser);

  const { t } = useTranslation();

  const title = t('systemInfo.system');

  useEffect(() => {
    const pathLengthMinutes = Number(plutoCtx?.pathLengthMinutes);
    setSliderIndex(minuteOptions.indexOf(pathLengthMinutes));
  }, [plutoCtx?.pathLengthMinutes]);

  const lengthOptionLabels = [
    t('general.disabledShort'),
    15 + t('general.dateTime.minute'),
    30 + t('general.dateTime.minute'),
    1 + t('general.dateTime.hour'),
    2 + t('general.dateTime.hour'),
    6 + t('general.dateTime.hour'),
  ];

  const minuteOptions = [0, 15, 30, 60, 120, 360];

  function handlePathLengthChange(_event: Event, newValue: number | number[]) {
    const index = newValue as number;
    setSliderIndex(index);
    const minutes = minuteOptions[index];
    plutoCtx?.setPathLengthMinutes(minutes);
  }

  if (!ctx) return;

  const body = (
    <CommonPageScrollableContent>
      {isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="target_attributes_title_label">
            {t('settings.targetAttributes')}
          </SettingsGroupTitle>

          <SettingsLink to={'/targetattreditor'}>
            <SettingsPaper clickable={true} data-qa-element-id="target_attributes_paper">
              <NewFeatureBadge releaseDate="2026-04-30" top="0px" right="32px">
                <DisplaySettings sx={settingsStartIconSx} />
              </NewFeatureBadge>

              <SettingsPaperTitle data-qa-element-id="target_attributes_label">
                {t('settings.editTargetAttributes')}
              </SettingsPaperTitle>

              <Edit sx={settingsIconSx} />
            </SettingsPaper>
          </SettingsLink>
        </div>
      )}
      <div>
        <SettingsGroupTitle data-qa-element-id="routes_title_label">{t('settings.path')}</SettingsGroupTitle>
        <SettingsColumnPaper>
          <SettingsPaperTitle data-qa-element-id="live_route_length_title">
            {t('settings.pathLengthMinutes')}
          </SettingsPaperTitle>
          <div style={{ padding: '0 15px' }}>
            <Slider
              min={0}
              max={minuteOptions.length - 1}
              step={1}
              marks={lengthOptionLabels.map((label, index) => ({
                value: index,
                label: <span data-qa-element-id={`route_length_slider_value_${index}`}>{label}</span>,
              }))}
              value={sliderIndex}
              onChange={handlePathLengthChange}
              data-qa-element-id="route_length_slider"
            />
          </div>
        </SettingsColumnPaper>
      </div>
    </CommonPageScrollableContent>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <CommonPageContainer data-qa-element-id="sysadmin_view">
      <CommonPageHeader data-qa-element-id="sysadmin_view_title">{title}</CommonPageHeader>
      {body}
    </CommonPageContainer>
  );
}
